import { createContext, useContext } from "react";

/**
 * QL-181. The seam between a dialog and the controls inside it.
 *
 * `DialogContent` guards dismissal — an outside click, Escape, the ✕ — behind a
 * "Discard changes?" prompt, but only once the dialog has been edited. Working
 * out whether it has been edited by watching DOM events is guesswork, and it
 * failed outright for keyboard users: picking a value in a Select or a
 * SearchableSelect with Enter dispatches no click, fires no `input`, and left
 * the dialog looking untouched right up to the moment it discarded the work.
 *
 * So the controls say so themselves. Every shared control that owns a value
 * calls `markDirty` from the change handler it already has, which is exact for
 * mouse and keyboard alike. The event heuristic in `dialog.tsx` stays
 * underneath as the floor for raw inputs and one-off buttons.
 *
 * `requestClose` is the same guard from the inside: `DialogCancel` uses it so
 * Cancel and the ✕ behave identically instead of one asking and one not.
 */
export type DialogGuard = {
  /** Record that the dialog now holds unsaved work. Idempotent. */
  markDirty: () => void;
  /**
   * Set the dirty state outright, including back to false. For a caller that
   * tracks its own dirty state and can therefore see an edit being *undone* —
   * `useDialogForm` compares against the values the dialog opened with, so
   * retyping the original value makes it clean again, which no one-way
   * `markDirty` can express. A caller that owns the dialog's fields owns this.
   */
  setDirty: (dirty: boolean) => void;
  /** Close the dialog, raising the discard prompt first if it is dirty. */
  requestClose: () => void;
};

export const DialogGuardContext = createContext<DialogGuard | null>(null);

const NO_DIALOG: DialogGuard = {
  markDirty: () => {},
  setDirty: () => {},
  requestClose: () => {},
};

/**
 * Shared controls call this unconditionally — they are used on plain pages as
 * often as in dialogs, and outside one it is an inert no-op.
 */
export function useDialogGuard(): DialogGuard {
  return useContext(DialogGuardContext) ?? NO_DIALOG;
}

/** True inside a `DialogContent` (every dialog variant renders one). */
export function useIsInDialog(): boolean {
  return useContext(DialogGuardContext) !== null;
}
